import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, Wallet, MessageCircle, Megaphone, BookOpen, Clock, BookText, Monitor,
  ChevronRight, Bell, HelpCircle, MapPin, ScanLine, Loader2, Check, School as SchoolIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * School Activities dashboard — student/parent facing.
 *
 * Renders the currently-joined school pulled from /schools/mine. The layout
 * and quick actions are universal — any school (private, government, gurukul,
 * international, coaching, online) renders inside the same shell. Users with
 * no joined school get a call-to-action into /schools to join or create one.
 */
const QUICK_ACTIONS = [
  { key: "attendance",    label: "Attendance",    icon: CalendarDays,   to: "/hr" },
  { key: "fee",           label: "Fee",           icon: Wallet,         to: "/wallet" },
  { key: "communication", label: "Communication", icon: MessageCircle,  to: "/messenger" },
  { key: "updates",       label: "Updates",       icon: Megaphone,      to: "/schools" },
  { key: "assignments",   label: "Assignments",   icon: BookOpen,       to: "/schools" },
  { key: "timetable",     label: "Timetable",     icon: Clock,          to: "/tasks" },
  { key: "journal",       label: "Class Journal", icon: BookText,       to: "/schools" },
  { key: "eLearning",     label: "e-Learning",    icon: Monitor,        to: "/learn" },
];

export default function School() {
  const { user } = useAuth();
  const first = user?.name?.split(" ")[0] || "Student";
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);   // null = loading · [] = none · [...] = list

  useEffect(() => {
    if (!user) { setMine([]); return; }
    api.get("/schools/mine")
      .then(r => setMine(r.data || []))
      .catch(() => setMine([]));
  }, [user]);

  const active = mine && mine.length > 0 ? mine[0] : null;
  const schoolName = active?.name || "My School";
  const schoolCity = active?.city || "";
  const schoolInitial = (schoolName.trim()[0] || "S").toUpperCase();
  const myRole = active?.my_role || null;                          // parent | teacher | admin | student
  const wardName = active?.my_child_name || null;                  // parents monitor this ward
  const wardGrade = active?.my_grade || null;
  const isParent = myRole === "parent";
  const isStaff  = myRole === "teacher" || myRole === "admin";
  // Greeting subject — parents see their ward's name; staff see the school; students see themselves.
  const greetingSubject = isParent && wardName ? wardName
                        : isStaff             ? schoolName
                        : (user?.name?.split(" ")[0] || "there");
  const roleLabel = isParent ? "Parent Portal" : isStaff ? `${myRole === 'admin' ? 'Admin' : 'Teacher'} View` : "Student View";

  const submitCode = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/school/attendance/checkin", { code });
      toast.success("Attendance marked ✓");
      setCode(""); setCheckinOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Check-in failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-3 py-4" data-testid="school-dashboard">
      {/* No joined school — universal empty state (schools + parents portal). */}
      {mine !== null && mine.length === 0 && (
        <div className="bg-white/70 backdrop-blur border border-stone-200 rounded-3xl p-6 text-center" data-testid="school-empty">
          <SchoolIcon className="mx-auto text-[#2D5A27]" size={40} />
          <h2 className="heading text-xl font-extrabold mt-3 text-stone-900">Parents & Schools Portal</h2>
          <p className="text-stone-500 text-sm mt-1">
            Monitor your ward's attendance, homework, fees, and school updates in one place. Works with every school — government, private, gurukul, international, coaching, online. Join with your school's 8-character code, or register a new one.
          </p>
          <Link to="/schools" className="inline-block mt-4 bg-[#2D5A27] text-white px-6 py-2.5 rounded-full font-bold text-sm" data-testid="school-join-cta">Open Schools</Link>
        </div>
      )}

      {/* School header — dynamic name / city / type from the user's joined school.
          Works for every school type: private, government, aided, gurukul,
          international, coaching, online. Parents see their ward highlighted;
          staff see the school as the subject. If the user has multiple joined
          schools, the header lets them switch. */}
      {active && (
      <div className="bg-white/70 backdrop-blur border border-stone-200 rounded-3xl p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2D5A27] to-emerald-700 grid place-items-center text-white font-black text-lg" data-testid="school-avatar">{schoolInitial}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase font-bold text-stone-500 tracking-widest">
            <span data-testid="school-role-label">{roleLabel}</span>
            <span className="text-stone-300">·</span>
            <span className="text-stone-500 normal-case tracking-normal">Monitor activities</span>
          </div>
          <div className="heading text-xl font-black text-stone-900 truncate" data-testid="school-name">{schoolName}</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {active.type && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-black text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid="school-type-badge">
                {active.type}
              </span>
            )}
            {active.board && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-black text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-full">{active.board}</span>
            )}
            {schoolCity && <span className="text-xs text-stone-500 inline-flex items-center gap-1"><MapPin size={11} /> {schoolCity}</span>}
          </div>
          {/* Ward strip — parents monitoring their child. */}
          {isParent && wardName && (
            <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2" data-testid="school-ward-strip">
              <div className="w-7 h-7 rounded-full bg-emerald-600 text-white grid place-items-center text-[11px] font-black">{wardName.trim()[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-widest">Monitoring ward</div>
                <div className="text-sm font-black text-stone-900 truncate" data-testid="school-ward-name">{wardName}{wardGrade && <span className="ml-2 text-xs font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded-full align-middle">Grade {wardGrade}</span>}</div>
              </div>
            </div>
          )}
          {mine.length > 1 && (
            <Link to="/schools" className="text-[11px] font-bold text-[#E05E36] hover:underline mt-1 inline-block" data-testid="school-switch">Switch school ({mine.length} linked) →</Link>
          )}
        </div>
        <button className="p-2 text-stone-500 hover:text-stone-900" title="Help"><HelpCircle size={18} /></button>
        <button className="p-2 text-stone-500 hover:text-stone-900" title="Alerts"><Bell size={18} /></button>
      </div>
      )}

      {/* Quick actions — visible only when a school is linked. */}
      {active && (
      <div className="mt-4 grid grid-cols-4 gap-3">
        {QUICK_ACTIONS.map(a => {
          const Icon = a.icon;
          return (
            <Link key={a.key} to={a.to} className="flex flex-col items-center gap-1.5 group" data-testid={`school-action-${a.key}`}>
              <div className="w-12 h-12 rounded-2xl bg-indigo-100 group-hover:bg-indigo-200 grid place-items-center text-indigo-600 transition-colors">
                <Icon size={20} />
              </div>
              <span className="text-[10px] font-bold text-stone-700 text-center leading-tight">{a.label}</span>
            </Link>
          );
        })}
      </div>
      )}

      {/* Notice */}
      {active && (
      <div className="mt-4 bg-white border border-stone-200 rounded-2xl px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-indigo-100 grid place-items-center text-indigo-600 shrink-0"><Megaphone size={14} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-stone-500">Notice</div>
          <div className="text-sm text-stone-800 truncate">Ask your teacher or school admin for any ERP support requests.</div>
        </div>
        <ChevronRight size={16} className="text-stone-400 shrink-0" />
      </div>
      )}

      {/* QR / Code check-in bar */}
      {active && (
      <button
        onClick={() => setCheckinOpen(true)}
        className="mt-3 w-full bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900 text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg"
        data-testid="school-checkin-btn"
      >
        <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center"><ScanLine size={20} /></div>
        <div className="text-left flex-1">
          <div className="font-black">Attendance Check-in</div>
          <div className="text-xs text-white/70">Enter the 6-digit class code from your teacher's screen</div>
        </div>
        <ChevronRight size={18} className="text-white/60" />
      </button>
      )}

      {checkinOpen && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => setCheckinOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-sm p-5 space-y-3" data-testid="school-checkin-modal">
            <div className="font-bold text-lg">Enter class code</div>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onPaste={e => {
                e.preventDefault();
                const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                setCode(pasted);
              }}
              inputMode="numeric"
              className="w-full text-center text-4xl font-black tracking-[0.5em] py-4 border-2 border-stone-300 rounded-2xl focus:border-emerald-600 outline-none font-mono"
              placeholder="______"
              data-testid="school-checkin-code"
            />
            <button
              onClick={submitCode}
              disabled={busy || code.length !== 6}
              className="w-full py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black text-sm inline-flex items-center justify-center gap-1.5"
              data-testid="school-checkin-submit"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} /> Check in</>}
            </button>
            <div className="text-[11px] text-stone-500 text-center">The code rotates every 90 seconds — ask your teacher for a fresh one if it expired.</div>
          </div>
        </div>
      )}

      {/* Greeting */}
      {active && (
      <>
      <div className="mt-5">
        <div className="heading text-xl font-black text-stone-900" data-testid="school-greeting">
          {isParent ? <>Monitoring <span className="text-[#2D5A27]">{greetingSubject}</span> 👋</>
                    : isStaff  ? <>Managing <span className="text-[#2D5A27]">{greetingSubject}</span> 👋</>
                                : <>Hello, {greetingSubject}! 👋</>}
        </div>
        <div className="text-xs text-stone-500">
          {isParent ? "Here's a quick overview of your ward's day"
                    : isStaff  ? "Here's a quick overview across your school"
                                : "Here's a quick overview about today"}
        </div>
      </div>

      {/* Metric cards */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <MetricCard color="indigo" icon={CalendarDays} label="Attendance Rate" value="87.27%" />
        <MetricCard color="amber"  icon={Wallet}       label="Outstanding Fee" value="₹0" />
        <MetricCard color="violet" icon={BookOpen}     label="Today's Assignments" value="0" />
        <MetricCard color="pink"   icon={CalendarDays} label="Today's Updates" value="0" />
      </div>

      <div className="mt-6 text-center text-[10px] text-stone-400">
        Find2Near · School Activities · Data will connect to your school ERP in Phase 2.
      </div>
      </>
      )}

      <Link to="/apps" className="mt-4 block text-center text-xs font-bold text-[#E05E36] hover:underline">← Back to All Apps</Link>
    </div>
  );
}

function MetricCard({ color, icon: Icon, label, value }) {
  const bgMap = { indigo: "bg-indigo-100 text-indigo-600", amber: "bg-amber-100 text-amber-600", violet: "bg-violet-100 text-violet-600", pink: "bg-pink-100 text-pink-600" };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className={`w-10 h-10 rounded-2xl grid place-items-center ${bgMap[color]}`}><Icon size={16} /></div>
      <div className="mt-3 text-2xl font-black text-stone-900">{value}</div>
      <div className="text-xs text-stone-500">{label}</div>
    </div>
  );
}
